import { useLanguage } from "../utils/LanguageContext";
import React, { useState, useEffect } from "react";
import { Search, Phone, Menu, X } from 'lucide-react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import { useAboutData } from "../hooks/useAboutData";
import axios from 'axios';
import SearchBox from "./SearchBox";

const API_BASE_URL = 'https://texla.uz/api/v1';

// Tilga qarab matn qaytarish
const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};

const Header = () => {
    const { about } = useAboutData();
    const phone = about?.phone || '+998...';
    const { language, setLanguage, t } = useLanguage();
    const [showCatalog, setShowCatalog] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showMobileCatalog, setShowMobileCatalog] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const navigate = useNavigate();

    // Kategoriyalarni olish - har ikki katalog uchun
    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        if (showCatalog || mobileMenuOpen || showMobileCatalog) {
            // Agar categories bo'sh bo'lsa, qayta yuklash
            if (categories.length === 0 && !loadingCategories) {
                fetchCategories();
            }
        }
    }, [showCatalog, mobileMenuOpen, showMobileCatalog, categories.length, loadingCategories]);

    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/categories/`);
            setCategories(response.data || []);
        } catch (error) {
            setCategories([]);
        } finally {
            setLoadingCategories(false);
        }
    };

    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location])

    const handleCategoryClick = (categoryId) => {
        window.scrollTo(0, 0);
        setShowCatalog(false);
        navigate(`/shop?category=${categoryId}`);
    };

    return (
        <div className="bg-white">
            {/* Catalog Sidebar */}
            {showCatalog && (
                <div className="fixed inset-0 z-50 flex">
                    <div className="w-64 bg-white shadow-2xl overflow-y-auto">
                        <div className="p-4 lg:p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg lg:text-xl font-bold">{t('header.catalog')}</h3>
                                <button onClick={() => setShowCatalog(false)}>
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="space-y-2">
                                {loadingCategories ? (
                                    <div className="text-center py-4">
                                        <p className="text-sm text-gray-500">{t('header.loading')}</p>
                                    </div>
                                ) : categories.length > 0 ? (
                                    // Scroll qo'shish - max 10 ta ko'rinadi
                                    <div className="max-h-80 overflow-y-auto">
                                        {categories.map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => handleCategoryClick(cat.id)}
                                                className="w-full text-left flex items-center gap-3 p-3 hover:bg-gray-100 rounded-lg transition text-sm lg:text-base font-medium text-gray-900"
                                            >
                                                {getLocalizedText(cat, language)}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-sm text-gray-500">{t('header.noCatalog')}</p>
                                        <p className="text-xs text-gray-400 mt-2">API: {API_BASE_URL}/categories/</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 bg-black/50" onClick={() => setShowCatalog(false)} />
                </div>
            )}

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-40 flex lg:hidden">
                    <div className="w-64 bg-white shadow-2xl overflow-y-auto">
                        <div className="p-4">
                            {/* Header - Menyu / Katalog Almashtirish */}
                            <div className="flex gap-2 mb-6">
                                <button
                                    onClick={() => setShowMobileCatalog(false)}
                                    className={`flex-1 py-2 px-3 rounded font-medium text-sm transition ${
                                        !showMobileCatalog
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                    }`}
                                >
                                    {t('header.menu')}
                                </button>
                                <button
                                    onClick={() => setShowMobileCatalog(true)}
                                    className={`flex-1 py-2 px-3 rounded font-medium text-sm transition ${
                                        showMobileCatalog
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                    }`}
                                >
                                    {t('header.catalog')}
                                </button>
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 hover:bg-gray-100 rounded"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* CATALOG VIEW */}
                            {showMobileCatalog ? (
                                <div>
                                    <div className="space-y-2">
                                        {loadingCategories ? (
                                            <p className="text-xs text-gray-500">{t('header.loading')}</p>
                                        ) : categories.length > 0 ? (
                                            // Scroll qo'shish - max 10 ta ko'rinadi
                                            <div className="max-h-64 overflow-y-auto">
                                                {categories.map(cat => (
                                                    <button
                                                        key={cat.id}
                                                        onClick={() => {
                                                            handleCategoryClick(cat.id);
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className="w-full text-left p-3 hover:bg-gray-100 rounded text-sm text-gray-900 font-medium"
                                                    >
                                                        {getLocalizedText(cat, language)}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-500 text-center py-4">{t('header.noCatalog')}</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* MENU VIEW */
                                <div>
                                    {/* Search in Mobile Menu */}
                                    <SearchBox isMobile={true} />

                                    {/* Navigation Links */}
                                    <div className="space-y-2 text-sm mb-6">
                                        <Link to="/about" className="block p-2 hover:bg-gray-100 rounded">{t('header.aboutCompany')}</Link>
                                        <Link to="/shop" className="block p-2 hover:bg-gray-100 rounded">{t('header.catalog')}</Link>
                                        <Link to="/blog" className="block p-2 hover:bg-gray-100 rounded">{t('header.news')}</Link>
                                        <Link to="/faqs" className="block p-2 hover:bg-gray-100 rounded">{t('header.faqs')}</Link>
                                        <Link to="/contact" className="block p-2 hover:bg-gray-100 rounded">{t('header.contact')}</Link>
                                    </div>

                                    {/* Language Selection */}
                                    <div className="border-t pt-4">
                                        <p className="text-xs text-gray-600 mb-2 font-medium">{t('language.languageLabel')}</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setLanguage('uz')}
                                                className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition ${
                                                    language === 'uz' ? 'bg-green-100 text-green-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'
                                                }`}
                                            >
                                                🇺🇿 {t('language.uz')}
                                            </button>
                                            <button
                                                onClick={() => setLanguage('ru')}
                                                className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition ${
                                                    language === 'ru' ? 'bg-red-100 text-red-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'
                                                }`}
                                            >
                                                🇷🇺 {t('language.ru')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
                </div>
            )}

            {/* Top Navigation */}
            <nav className="hidden lg:block bg-white border-b">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between h-12 text-sm">
                        <div className="flex gap-4 overflow-x-auto">
                            <Link to="/about" className="block p-2 hover:bg-gray-100 rounded">{t('header.aboutCompany')}</Link>
                            <Link to="/shop" className="block p-2 hover:bg-gray-100 rounded">{t('header.catalog')}</Link>
                            <Link to="/blog" className="block p-2 hover:bg-gray-100 rounded">{t('header.news')}</Link>
                            <Link to="/faqs" className="block p-2 hover:bg-gray-100 rounded">{t('header.faqs')}</Link>
                            <Link to="/contact" className="block p-2 hover:bg-gray-100 rounded">{t('header.contact')}</Link>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setLanguage('uz')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${
                                    language === 'uz' ? 'bg-green-100 text-green-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                            >
                                🇺🇿 {t('language.uz')}
                            </button>
                            <button
                                onClick={() => setLanguage('ru')}
                                className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition ${
                                    language === 'ru' ? 'bg-red-100 text-red-900 font-bold' : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                            >
                                🇷🇺 {t('language.ru')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Header */}
            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 lg:py-4">
                    <div className="flex items-center justify-between gap-2 lg:gap-4 flex-wrap">
                        <Link to={"/"}>
                            <div className="flex items-center gap-2">
                                <div className="w-16 h-16 lg:w-24 lg:h-24 bg-blue-600 rounded flex items-center justify-center">
                                    {about?.logo && (
                                        <img
                                            src={about.logo}
                                            alt="Logo"
                                            className="w-16 h-16 lg:w-24 lg:h-24 rounded object-contain bg-white"
                                        />
                                    )}
                                </div>
                            </div>
                        </Link>
                        <button
                            onClick={() => setShowCatalog(!showCatalog)}
                            className="hidden sm:flex lg:flex items-center gap-2 bg-white border border-gray-300 text-gray-900 px-3 lg:px-6 py-2 rounded-lg hover:bg-gray-50 transition font-medium text-sm lg:text-base"
                        >
                            <Menu size={18} />
                            <span>{t('header.catalog')}</span>
                        </button>

                        <div className="hidden sm:flex flex-1 min-w-[200px] max-w-sm lg:max-w-xl">
                            <SearchBox isMobile={false} />
                        </div>

                        <div className="hidden md:block text-right text-sm lg:text-base">
                            <div className="text-xs lg:text-sm text-gray-600">{t('header.workingHours')}</div>
                            <a href={`tel:${phone}`} className="text-sm lg:text-lg font-bold text-gray-900 flex items-center gap-1 whitespace-nowrap">
                                <Phone size={16} className="text-orange-600" />
                                {phone}
                            </a>
                        </div>

                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            <Menu size={24} />
                        </button>
                    </div>
                </div>
            </header>
        </div>
    );
};

export default Header;